import React from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { hideContractPaymentNotification } from "../../redux/Slices/notificationSlice";
import { CheckCircle, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ContractPaymentNotificationDialogProps {
    visible: boolean;
    onHide: () => void;
    contractData?: {
        contractId: string;
        amount: number;
        currency: string;
        professionalName: string;
        clientName: string;
        chatId?: string;
    };
}

const ContractPaymentNotificationDialog: React.FC<ContractPaymentNotificationDialogProps> = ({
    visible,
    onHide,
    contractData
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Debug logging
    console.log("ContractPaymentNotificationDialog render:", { visible, contractData });

    const handleNavigateToChat = () => {
        console.log("ContractPaymentNotificationDialog: Navigating to chat");
        
        window.location.href = "/MessageAndVideoCall";
        //   navigate("/MessageAndVideoCall");
        onHide();
    };

    const handleClose = () => {
        console.log("ContractPaymentNotificationDialog: Closing dialog");
        onHide();
    };

    return (
        <Dialog
            header={
                <div className="flex items-center gap-3">
                    <CheckCircle className="h-8 w-8 text-green-500" />
                    <span className="text-xl font-semibold text-gray-800">{t("contractPaymentDialog.title")}</span>
                </div>
            }
            visible={visible}
            onHide={handleClose}
            style={{ width: "450px" }}
            modal
            className="p-dialog-maximized-responsive"
            draggable={false}
            resizable={false}
            closable={true}
            dismissableMask={true}
        >
            <div className="p-4">
                <div className="text-center mb-6">
                    <div className="bg-green-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="h-12 w-12 text-green-500" />
                    </div>

                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        {t("contractPaymentDialog.successTitle")}
                    </h3>

                    <p className="text-gray-600 mb-4">
                        {t("contractPaymentDialog.successMessage")}
                    </p>
                </div>

                {contractData && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-600">{t("contractPaymentDialog.contractId")}</span>
                                <span className="text-sm text-gray-800 font-mono">
                                    {contractData.contractId.substring(0, 8)}...
                                </span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-600">{t("contractPaymentDialog.amount")}</span>
                                <span className="text-sm font-semibold text-green-600">
                                    {contractData.currency} {contractData.amount.toLocaleString()}
                                </span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-600">{t("contractPaymentDialog.professional")}</span>
                                <span className="text-sm text-gray-800">{contractData.professionalName}</span>
                            </div>

                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-600">{t("contractPaymentDialog.client")}</span>
                                <span className="text-sm text-gray-800">{contractData.clientName}</span>
                            </div>
                        </div>
                    </div>
                )}
                <p className="text-red-600 text-sm mb-4">{t("contractPaymentDialog.reminder")}</p>
                <div className="flex flex-col gap-3">
                    <Button
                        label={t("contractPaymentDialog.goToChat")}
                        onClick={handleNavigateToChat}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white border-0 py-3"
                        size="large"
                    />

                    <Button
                        label={t("contractPaymentDialog.close")}
                        onClick={handleClose}
                        className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 border-0 py-3"
                        size="large"
                        outlined
                    />
                </div>

                <div className="mt-4 text-center">
                    <p className="text-xs text-gray-500">
                        {t("contractPaymentDialog.accessMessage")}
                    </p>
                </div>
            </div>
        </Dialog>
    );
};

export default ContractPaymentNotificationDialog;
